import { useCallback, useRef, useState } from "react";

type CopyCallbacks = {
  /** Called after a successful clipboard write. */
  onSuccess?: () => void;
  /** Called if the clipboard write throws (permission denied, no API, etc.). */
  onError?: (error: unknown) => void;
};

/**
 * Clipboard copy with a transient "copied" flag for button affordances.
 *
 * `copied` flips to `true` on a successful write and auto-resets after
 * `resetMs` (default 2s). Callers own their own messaging via the optional
 * onSuccess/onError callbacks, so this stays UI-agnostic (some call sites toast,
 * some just flip the button label).
 */
export const useCopyToClipboard = (resetMs = 2000) => {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const copy = useCallback(
    async (text: string, { onSuccess, onError }: CopyCallbacks = {}) => {
      try {
        await navigator.clipboard.writeText(text);
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), resetMs);
        onSuccess?.();
      } catch (error) {
        onError?.(error);
      }
    },
    [resetMs]
  );

  return { copied, copy };
};
